import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPermission } from "@/lib/permissions";
import { Navbar } from "@/components/Navbar";
import { IntroNote } from "@/components/IntroNote";
import { GlossaryTerm } from "@/components/GlossaryTerm";
import { ConfidenceBadge, confidenceBand } from "@/components/ConfidenceBadge";
import { SupplierProfileDrawer } from "@/components/SupplierProfileDrawer";
import { SupplierMap } from "@/components/SupplierMap";
import { SupplyChainIntel } from "@/components/SupplyChainIntel";
import { CustomerMasterUpload } from "@/components/CustomerMasterUpload";
import { SupplierForm } from "@/components/SupplierForm";
import { toast } from "sonner";
import { ChevronRight, Factory, MapPin, Network, Pencil, Plus, ShieldAlert } from "lucide-react";

const TIER_STYLES = {
  0: "bg-ek-purple text-white border-ek-purple",
  1: "bg-ek-purple/10 text-ek-purple border-ek-purple/20",
  2: "bg-blue-50 text-blue-700 border-blue-200",
  3: "bg-gray-100 text-gray-600 border-gray-200",
};

export default function Suppliers() {
  const { user } = useAuth();
  const canRaise = hasPermission(user, "risk.write");
  const canImportMaster = hasPermission(user, "supplier.write");
  const [formMode, setFormMode] = useState(null); // null | 'create' | 'edit'
  const [formInitial, setFormInitial] = useState(null);
  const [roots, setRoots] = useState([]);
  const [rootId, setRootId] = useState(null);
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  const [profileId, setProfileId] = useState(null);
  const [allSuppliers, setAllSuppliers] = useState([]);

  useEffect(() => {
    api
      .get("/suppliers?limit=500")
      .then(({ data }) => setAllSuppliers(data.items))
      .catch(() => {});
  }, []);

  const loadGraph = useCallback((id) => {
    setLoading(true);
    api
      .get(`/suppliers/${id}/graph?depth=2`)
      .then(({ data }) => {
        setGraph(data);
        setExpanded(new Set([data.root.supplier_id])); // root expanded → shows tier-1
      })
      .catch(() => toast.error("Failed to load supply chain graph"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api
      .get("/suppliers?tier=0")
      .then(({ data }) => {
        setRoots(data.items);
        if (data.items[0]) {
          setRootId(data.items[0].supplier_id);
          loadGraph(data.items[0].supplier_id);
        } else {
          setLoading(false);
        }
      })
      .catch(() => {
        toast.error("Failed to load suppliers");
        setLoading(false);
      });
  }, [loadGraph]);

  const { byId, childrenMap } = useMemo(() => {
    if (!graph) return { byId: {}, childrenMap: {} };
    const ids = {};
    ids[graph.root.supplier_id] = graph.root;
    graph.nodes.forEach((n) => (ids[n.supplier_id] = n));
    const map = {};
    graph.edges.forEach((e) => {
      if (!ids[e.child_supplier_id]) return;
      (map[e.parent_supplier_id] = map[e.parent_supplier_id] || []).push(e);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => b.confidence - a.confidence));
    return { byId: ids, childrenMap: map };
  }, [graph]);

  // Flatten the tree into a visible, depth-annotated row list (iterative, no recursive component).
  const rows = useMemo(() => {
    if (!graph) return [];
    const out = [];
    const walk = (id, depth, edge) => {
      const node = byId[id];
      if (!node) return;
      const kids = childrenMap[id] || [];
      out.push({ node, edge, depth, kidCount: kids.length });
      if (expanded.has(id)) kids.forEach((e) => walk(e.child_supplier_id, depth + 1, e));
    };
    walk(graph.root.supplier_id, 0, null);
    return out;
  }, [graph, byId, childrenMap, expanded]);

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const stats = useMemo(() => {
    if (!graph) return { total: 0, edges: 0, low: 0, depth: 0 };
    const tiers = new Set(graph.nodes.map((n) => n.tier));
    const low = graph.edges.filter((e) => confidenceBand(e.confidence) === "low").length;
    return { total: graph.nodes.length, edges: graph.edges.length, low, depth: tiers.size };
  }, [graph]);

  const reloadAfterMutate = useCallback(() => {
    // Cheapest path: reload the current root's graph (covers add/edit/delete).
    if (rootId) loadGraph(rootId);
    // Also refresh the tier-0 list in case a new root was added.
    api.get("/suppliers?tier=0").then(({ data }) => setRoots(data.items)).catch(() => {});
  }, [rootId, loadGraph]);

  const openCreate = () => {
    setFormInitial(null);
    setFormMode("create");
  };
  const openEdit = (node) => {
    setFormInitial(node);
    setFormMode("edit");
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-5xl px-6 py-12" data-testid="suppliers-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="ek-eyebrow">N-Tier Supply Chain</p>
            <h1 className="ek-heading mt-2 text-4xl sm:text-5xl">Supply Chain Explorer</h1>
            <p className="mt-3 text-base text-ek-ink/65">
              Hierarchical third-party risk graph with confidence-scored, inferred relationships.
            </p>
          </div>
          {canImportMaster && (
            <button
              type="button"
              onClick={openCreate}
              data-testid="supplier-add-button"
              className="inline-flex items-center gap-1.5 rounded-md bg-ek-purple px-3 py-2 text-sm font-medium text-white hover:bg-ek-purple/90"
            >
              <Plus size={14} /> Add supplier
            </button>
          )}
        </div>

        <IntroNote icon={Network} title="What you're looking at" testid="suppliers-intro" className="mt-6">
          Your supply chain mapped beyond tier-1 into the sub-suppliers you depend on. Relationships are
          confidence-scored, single-points-of-failure are flagged, and each node carries a risk profile. This is
          where hidden concentration and fourth-party exposure become visible before they bite.
        </IntroNote>

        {roots.length > 1 && (
          <div className="mt-6 flex flex-wrap gap-2" data-testid="supplier-root-switcher">
            {roots.map((r) => (
              <button
                key={r.supplier_id}
                onClick={() => {
                  setRootId(r.supplier_id);
                  loadGraph(r.supplier_id);
                }}
                className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                  rootId === r.supplier_id
                    ? "border-ek-purple bg-ek-purple/10 text-ek-purple"
                    : "border-black/10 text-ek-ink/60 hover:border-ek-purple/40"
                }`}
              >
                {r.legal_name}
              </button>
            ))}
          </div>
        )}

        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="supplier-stats">
          <StatCard icon={Network} label="Suppliers mapped" value={stats.total} tipKey="N-tier supply chain" />
          <StatCard icon={Factory} label="Relationships" value={stats.edges} />
          <StatCard icon={ChevronRight} label="Tier depth" value={stats.depth} tipKey="Supplier tier" />
          <StatCard icon={ShieldAlert} label="Low-confidence links" value={stats.low} accent={stats.low > 0} tipKey="N-tier supply chain" />
        </div>

        {allSuppliers.length > 0 && (
          <div className="mt-8">
            <SupplierMap suppliers={allSuppliers} />
          </div>
        )}

        <SupplyChainIntel onSelect={(id) => setProfileId(id)} canRaise={canRaise} />

        {canImportMaster && <CustomerMasterUpload />}

        <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-ek-ink/65">
          <span className="font-medium text-ek-ink/60">Link confidence:</span>
          <LegendDot className="bg-emerald-500" label="High ≥75%" />
          <LegendDot className="bg-amber-500" label="Medium 50–74%" />
          <LegendDot className="bg-red-500" label="Low <50%" />
        </div>

        <div className="mt-6">
          {loading ? (
            <div className="rounded-md border border-black/5 bg-white p-12 text-center text-sm text-ek-ink/65">
              Loading supply chain…
            </div>
          ) : rows.length ? (
            <div className="space-y-1.5" data-testid="supplier-tree">
              {rows.map(({ node, edge, depth, kidCount }) => {
                const isOpen = expanded.has(node.supplier_id);
                return (
                  <div
                    key={node.supplier_id}
                    data-testid="supplier-node"
                    data-tier={node.tier}
                    className="flex items-center gap-3 rounded-xl border border-black/5 bg-white px-3 py-2.5 transition-colors hover:bg-ek-purple/[0.03]"
                    style={{ marginLeft: depth * 22 }}
                  >
                    <button
                      onClick={() => kidCount > 0 && toggle(node.supplier_id)}
                      data-testid={`supplier-toggle-${node.supplier_id}`}
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md ${
                        kidCount > 0 ? "text-ek-purple hover:bg-ek-purple/10" : "text-transparent"
                      }`}
                    >
                      <ChevronRight size={15} className={`transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    </button>

                    <span
                      className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                        TIER_STYLES[node.tier] || TIER_STYLES[3]
                      }`}
                    >
                      T{node.tier}
                    </span>

                    <button
                      onClick={() => setProfileId(node.supplier_id)}
                      data-testid={`supplier-row-${node.supplier_id}`}
                      className="min-w-0 flex-1 rounded-lg px-1 py-0.5 text-left transition-colors hover:bg-ek-purple/[0.04]"
                      title="View risk profile"
                    >
                      <p className="truncate text-sm font-semibold text-ek-ink hover:text-ek-purple">{node.legal_name}</p>
                      <div className="mt-0.5 flex items-center gap-3 text-[11px] text-ek-ink/65">
                        <span className="flex items-center gap-1">
                          <MapPin size={10} /> {node.country}
                        </span>
                        <span className="font-mono">NAICS {node.industry_naics}</span>
                        {node.duns ? (
                          <span className="font-mono">DUNS {node.duns}</span>
                        ) : (
                          <span className="italic text-ek-ink/30">no DUNS</span>
                        )}
                      </div>
                    </button>

                    {edge && <ConfidenceBadge confidence={edge.confidence} inferredVia={edge.inferred_via} />}
                    {kidCount > 0 && (
                      <span className="shrink-0 rounded-full bg-ek-canvas px-2 py-0.5 text-[10px] font-medium text-ek-ink/65">
                        {kidCount}
                      </span>
                    )}
                    {canImportMaster && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(node);
                        }}
                        title="Edit supplier"
                        className="shrink-0 rounded-md p-1 text-ek-ink/40 hover:bg-ek-purple/10 hover:text-ek-purple"
                        data-testid={`supplier-edit-${node.supplier_id}`}
                      >
                        <Pencil size={12} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-md border border-black/5 bg-white p-12 text-center text-sm text-ek-ink/65">
              No supply chain data available.
            </div>
          )}
        </div>
      </main>

      <SupplierProfileDrawer supplierId={profileId} onClose={() => setProfileId(null)} />

      <SupplierForm
        open={formMode !== null}
        onOpenChange={(o) => !o && setFormMode(null)}
        mode={formMode || "create"}
        initial={formInitial}
        onSaved={() => reloadAfterMutate()}
      />
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, accent, tipKey }) => (
  <div
    className={`rounded-md border bg-white p-4 ${accent ? "border-red-200" : "border-black/5"}`}
    data-testid={`supplier-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
  >
    <Icon size={16} className={accent ? "text-red-500" : "text-ek-purple"} />
    <p className="mt-2 font-display text-2xl font-semibold text-ek-ink">{value}</p>
    <p className="text-[11px] text-ek-ink/65">
      {tipKey ? <GlossaryTerm k={tipKey} label={label} /> : label}
    </p>
  </div>
);

const LegendDot = ({ className, label }) => (
  <span className="flex items-center gap-1.5">
    <span className={`h-2.5 w-2.5 rounded-full ${className}`} />
    {label}
  </span>
);
